import React, { useState, useEffect } from 'react'
import { TimeEntry, Project } from '../../types'
import { X, Clock, Calendar, FileText, AlertCircle, Edit3 } from 'lucide-react'
import toast from 'react-hot-toast'

interface EditTimeEntryModalProps {
  isOpen: boolean
  entry: TimeEntry | null
  onClose: () => void
  onSubmit: (data: EditTimeEntryData) => Promise<void>
  projects: Project[]
  isLoading?: boolean
}

export interface EditTimeEntryData {
  id: string
  projectId: string
  description: string
  startTime: string
  endTime: string
  originalDate?: string
}

const EditTimeEntryModal: React.FC<EditTimeEntryModalProps> = ({
  isOpen,
  entry,
  onClose,
  onSubmit,
  projects,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<EditTimeEntryData>({
    id: '',
    projectId: '',
    description: '',
    startTime: '09:00',
    endTime: '17:00'
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [calculatedDuration, setCalculatedDuration] = useState<string>('')

  // 當 entry 變更時初始化表單資料
  useEffect(() => {
    if (entry && isOpen) {
      const formatTime = (dateTime: string) => {
        // 確保使用用戶本地時區格式化時間
        const date = new Date(dateTime)
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      setFormData({
        id: entry.id,
        projectId: entry.projectId,
        description: entry.description || '',
        startTime: formatTime(entry.startTime),
        endTime: entry.endTime ? formatTime(entry.endTime) : '17:00'
      })
    }
  }, [entry, isOpen])

  // 計算工時
  useEffect(() => {
    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end > start) {
        const diffMs = end.getTime() - start.getTime()
        const hours = Math.floor(diffMs / (1000 * 60 * 60))
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
        setCalculatedDuration(`${hours}h ${minutes}m`)
      } else {
        setCalculatedDuration('無效時間')
      }
    }
  }, [formData.startTime, formData.endTime])

  // 表單驗證
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.projectId) {
      newErrors.projectId = '請選擇專案'
    }

    if (!formData.startTime) {
      newErrors.startTime = '請選擇開始時間'
    }

    if (!formData.endTime) {
      newErrors.endTime = '請選擇結束時間'
    }

    if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}:00`)
      const end = new Date(`2000-01-01T${formData.endTime}:00`)
      
      if (end <= start) {
        newErrors.endTime = '結束時間必須晚於開始時間'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 處理表單提交
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      await onSubmit(formData)
      handleClose()
      toast.success('時間記錄已更新')
    } catch (error) {
      toast.error('更新時間記錄失敗')
    }
  }

  // 處理關閉
  const handleClose = () => {
    setFormData({
      id: '',
      projectId: '',
      description: '',
      startTime: '09:00',
      endTime: '17:00'
    })
    setErrors({})
    setCalculatedDuration('')
    onClose()
  }

  // 處理輸入變更
  const handleInputChange = (field: keyof EditTimeEntryData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // 清除對應的錯誤
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  if (!isOpen || !entry) return null

  // 格式化日期顯示
  const formatDate = (dateTime: string) => {
    const date = new Date(dateTime)
    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-secondary-800 flex items-center">
            <Edit3 className="w-5 h-5 mr-2" />
            編輯時間記錄
          </h2>
          <button
            onClick={handleClose}
            className="p-1 text-secondary-400 hover:text-secondary-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 日期顯示 */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center text-blue-800">
              <Calendar className="w-4 h-4 mr-2" />
              <span className="text-sm">
                編輯日期：{formatDate(entry.startTime)}
              </span>
            </div>
          </div>

          {/* 專案選擇 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              專案選擇 <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => handleInputChange('projectId', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                errors.projectId ? 'border-red-500' : 'border-secondary-300'
              }`}
            >
              <option value="">選擇專案...</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            {errors.projectId && (
              <p className="mt-1 text-sm text-red-600">{errors.projectId}</p>
            )}
          </div>

          {/* 工作描述 */}
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-2">
              工作描述
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="輸入工作內容..."
              className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={3}
            />
          </div>

          {/* 時間設定 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                開始時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.startTime}
                onChange={(e) => handleInputChange('startTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.startTime ? 'border-red-500' : 'border-secondary-300'
                }`}
              />
              {errors.startTime && (
                <p className="mt-1 text-sm text-red-600">{errors.startTime}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                結束時間 <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={formData.endTime}
                onChange={(e) => handleInputChange('endTime', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.endTime ? 'border-red-500' : 'border-secondary-300'
                }`}
              />
              {errors.endTime && (
                <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
              )}
            </div>
          </div>

          {/* 計算工時顯示 */}
          {calculatedDuration && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center text-blue-800">
                <Clock className="w-4 h-4 mr-2" />
                <span className="text-sm">計算工時：{calculatedDuration}</span>
              </div>
            </div>
          )}

          {/* 標籤顯示 */}
          <div className="flex flex-wrap gap-2">
            {entry.isManual && (
              <div className="flex items-center bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-xs font-medium">
                <FileText className="w-3 h-3 mr-1" />
                <span>手動新增</span>
              </div>
            )}
            {entry.isEdited && (
              <div className="flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                <Edit3 className="w-3 h-3 mr-1" />
                <span>已編輯</span>
              </div>
            )}
          </div>

          {/* 提示訊息 */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">注意事項：</p>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>編輯後此記錄將標記為「已編輯」</li>
                  <li>請確保時間設定正確，避免與其他記錄衝突</li>
                  <li>結束時間必須晚於開始時間</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 按鈕 */}
          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 border border-secondary-300 text-secondary-700 rounded-lg hover:bg-secondary-50 transition-colors"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? '更新中...' : '確認更新'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditTimeEntryModal
